import React, { useRef, useEffect, useState } from "react";

interface ScratchRevealProps {
  cardId: string | number;
  rewardText?: string;
  amount?: number | string;
  scratched: boolean;
  onScratchComplete: (cardId: string | number) => void;
}

const ScratchReveal: React.FC<ScratchRevealProps> = ({
  cardId,
  rewardText = "You won 🎉",
  amount,
  onScratchComplete,
  scratched,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isRevealed, setIsRevealed] = useState(scratched || false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const rect = container.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    // Fill metallic gradient overlay
    const gradient = ctx.createLinearGradient(0, 0, rect.width, rect.height);
    gradient.addColorStop(0, "#d9d9d9");
    gradient.addColorStop(1, "#a6a6a6");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, rect.width, rect.height);

    let isDrawing = false;

    const getPosition = (e: MouseEvent | TouchEvent) => {
      const rect = canvas.getBoundingClientRect();
      const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
      const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
      return { x: clientX - rect.left, y: clientY - rect.top };
    };

    const startDrawing = (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      isDrawing = true;
    };

    const stopDrawing = () => {
      isDrawing = false;

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      let cleared = 0;
      for (let i = 3; i < imageData.data.length; i += 4) {
        if (imageData.data[i] === 0) cleared++;
      }
      const clearedPercent = (cleared / (canvas.width * canvas.height)) * 100;

      if (clearedPercent >= 50 && !isRevealed) {
        setIsRevealed(true);
        onScratchComplete?.(cardId);
      }
    };

    const draw = (e: MouseEvent | TouchEvent) => {
      if (!isDrawing) return;
      const pos = getPosition(e);
      ctx.globalCompositeOperation = "destination-out";
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, 15, 0, Math.PI * 2);
      ctx.fill();
    };

    // Add listeners
    canvas.addEventListener("mousedown", startDrawing);
    canvas.addEventListener("touchstart", startDrawing);
    canvas.addEventListener("mousemove", draw);
    canvas.addEventListener("touchmove", draw);
    canvas.addEventListener("mouseup", stopDrawing);
    canvas.addEventListener("touchend", stopDrawing);

    // Cleanup properly when card changes or modal closes
    return () => {
      canvas.removeEventListener("mousedown", startDrawing);
      canvas.removeEventListener("touchstart", startDrawing);
      canvas.removeEventListener("mousemove", draw);
      canvas.removeEventListener("touchmove", draw);
      canvas.removeEventListener("mouseup", stopDrawing);
      canvas.removeEventListener("touchend", stopDrawing);
      ctx.clearRect(0, 0, rect.width, rect.height);
    };
  }, [cardId, isRevealed, onScratchComplete]);

  return (
    <div
      key={cardId} // ensures re-mount for each new card
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        height: "180px",
        borderRadius: "12px",
        overflow: "hidden",
        boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
        margin: "20px auto",
        background: "linear-gradient(135deg, #e0ffe0, #b2f5ea)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        fontSize: "20px",
        fontWeight: 600,
        color: "#52c41a",
        textAlign: "center",
      }}
    >
      {isRevealed ? (
        <div style={{ opacity: 1, transition: "opacity 0.5s ease-in-out" }}>
          🎉 {rewardText}
          {amount && (
            <div style={{ fontSize: "18px", marginTop: "8px" }}>
              {Number(amount).toFixed(2)} <sub>HVT</sub>
            </div>
          )}
        </div>
      ) : (
        <>Scratch to reveal your reward!</>
      )}

      {!isRevealed && (
        <canvas
          ref={canvasRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            borderRadius: "12px",
            touchAction: "none",
            cursor: "grab",
          }}
        />
      )}
    </div>
  );
};

export default ScratchReveal;
